/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import { Search, X, UserPlus, CheckCircle2 } from "lucide-react";
import { usersApi, villaApi } from "../auth/api";
import "./OnboardUserModal.css";

const ROLE_MAP = {
  primary: "PRIMARY_OWNER",
  resident: "RESIDENT",
  tenant: "TENANT",
};

const ROLE_LABEL = {
  primary: "Primary Owner",
  resident: "Resident",
  tenant: "Tenant",
};

export default function OnboardUserModal({ open, onClose, onSuccess }) {
  const [villas, setVillas] = useState([]);
  const [search, setSearch] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedVilla, setSelectedVilla] = useState(null);
  const [role, setRole] = useState("primary");
  const [relation, setRelation] = useState("");
  const [tenantEndDate, setTenantEndDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [createdUser, setCreatedUser] = useState(null);

  useEffect(() => {
    if (!open) {
      setPhone("");
      setName("");
      setEmail("");
      setSearch("");
      setSelectedVilla(null);
      setRole("primary");
      setRelation("");
      setTenantEndDate("");
      setError("");
      setSubmitting(false);
      setCreatedUser(null);
      return;
    }
    const token = localStorage.getItem("access_token");
    villaApi
      .list(token)
      .then(setVillas)
      .catch((e) => setError(e.message));
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return villas;
    const q = search.toLowerCase();
    return villas.filter((v) => v.villa_number.toLowerCase().includes(q));
  }, [villas, search]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[0-9]{10}$/.test(phone))
      return setError("Phone must be 10 digits");
    if (!name.trim()) return setError("Name is required");
    if (!selectedVilla) return setError("Please pick a villa");
    if (role === "tenant" && !tenantEndDate)
      return setError("Tenant requires an end date");
    setSubmitting(true);
    try {
      const token = localStorage.getItem("access_token");
      const u = await usersApi.onboard(
        {
          phone_number: phone,
          name: name.trim(),
          email: email.trim() || null,
          villa_id: selectedVilla.id,
          role: ROLE_MAP[role],
          relation: relation || null,
          tenant_end_date: role === "tenant" ? tenantEndDate : null,
        },
        token
      );
      setCreatedUser(u);
      onSuccess && onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (createdUser) {
    return (
      <div className="onb-modal-overlay" onClick={onClose}>
        <div
          className="onb-modal onb-modal-success"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="onb-success-icon">
            <CheckCircle2 size={40} strokeWidth={1.7} />
          </div>
          <h2>Member onboarded</h2>
          <p>
            <strong>{createdUser.name}</strong> is now an approved{" "}
            {ROLE_LABEL[role].toLowerCase()} of{" "}
            <strong>{selectedVilla.villa_number}</strong>.
          </p>
          <p className="onb-success-hint">
            They can sign in directly using <strong>Sign in with WhatsApp OTP</strong> with
            their number <code>+91 {createdUser.phone_number}</code>.
            No password is required.
          </p>
          <div className="onb-modal-actions">
            <button
              className="onb-btn-secondary"
              onClick={() => {
                setCreatedUser(null);
                setPhone("");
                setName("");
                setEmail("");
                setSelectedVilla(null);
                setRelation("");
                setTenantEndDate("");
                setError("");
              }}
            >
              Onboard another
            </button>
            <button className="onb-btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onb-modal-overlay" onClick={onClose}>
      <div className="onb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="onb-modal-head">
          <h2>
            <UserPlus size={18} /> Onboard a member
          </h2>
          <button className="onb-modal-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <p className="onb-modal-sub">
          Create an approved account for a villa owner, family member, or tenant.
          They can sign in immediately via WhatsApp OTP.
        </p>

        <form onSubmit={submit}>
          <div className="onb-grid">
            <div className="onb-field">
              <label>Full name <span className="req">*</span></label>
              <input
                type="text"
                className="onb-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
              />
            </div>
            <div className="onb-field">
              <label>Phone number <span className="req">*</span></label>
              <div className="onb-phone-row">
                <span>+91</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                  placeholder="10-digit mobile"
                />
              </div>
            </div>
          </div>

          <div className="onb-field">
            <label>Email (optional)</label>
            <input
              type="email"
              className="onb-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>

          <div className="onb-field">
            <label>Villa <span className="req">*</span></label>
            <div className="onb-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search by villa number…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="onb-villa-list">
              {filtered.length === 0 ? (
                <div className="onb-empty">No villas match</div>
              ) : (
                filtered.slice(0, 60).map((v) => (
                  <button
                    type="button"
                    key={v.id}
                    className={`onb-villa-item ${
                      selectedVilla?.id === v.id ? "selected" : ""
                    }`}
                    onClick={() => setSelectedVilla(v)}
                  >
                    <strong>{v.villa_number}</strong>
                    <span>{v.address || "Tukkuguda"}</span>
                  </button>
                ))
              )}
            </div>
            {selectedVilla && (
              <div className="onb-selected">
                Selected: <strong>{selectedVilla.villa_number}</strong>
              </div>
            )}
          </div>

          <div className="onb-field">
            <label>Role <span className="req">*</span></label>
            <div className="onb-role-row">
              {["primary", "resident", "tenant"].map((r) => (
                <button
                  type="button"
                  key={r}
                  className={`onb-role-pill ${role === r ? "selected" : ""}`}
                  onClick={() => setRole(r)}
                >
                  {ROLE_LABEL[r]}
                </button>
              ))}
            </div>
          </div>

          {role === "resident" && (
            <div className="onb-field">
              <label>Relation to owner</label>
              <input
                type="text"
                className="onb-input"
                value={relation}
                onChange={(e) => setRelation(e.target.value)}
                placeholder="e.g. Spouse, Son, Daughter"
              />
            </div>
          )}

          {role === "tenant" && (
            <div className="onb-field">
              <label>Tenancy end date <span className="req">*</span></label>
              <input
                type="date"
                className="onb-input"
                value={tenantEndDate}
                onChange={(e) => setTenantEndDate(e.target.value)}
              />
            </div>
          )}

          {error && <div className="onb-error">{error}</div>}

          <div className="onb-modal-actions">
            <button
              type="button"
              className="onb-btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="onb-btn-primary" disabled={submitting}>
              {submitting ? "Onboarding…" : "Onboard member"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
